import {
	BoldOutlined,
	CheckOutlined,
	CloseOutlined,
	DisconnectOutlined,
	ItalicOutlined,
	LinkOutlined,
	OrderedListOutlined,
	UnorderedListOutlined,
} from "@ant-design/icons"
import HardBreak from "@tiptap/extension-hard-break"
import LinkExtension from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import { Editor, EditorContent, useEditor } from "@tiptap/react"
import { BubbleMenu } from "@tiptap/react/menus"
import StarterKit from "@tiptap/starter-kit"
import { FormItemInputContext } from "antd/es/form/context"
import { Button } from "components/Button/Button"
import { Divider } from "components/Divider/Divider"
import {
	ComponentProps,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { cn } from "styles/cn"

export interface InputRTEProps {
	id?: string
	value?: string
	onChange?: (value: string) => void
	placeholder?: string
	disabled?: boolean
	readOnly?: boolean
	autoFocus?: boolean
	status?: "error" | "warning"
	className?: string
	minRows?: number
	maxRows?: number
	variant?: "outline" | "borderless"
	"aria-label"?: string
}

export function InputRTE({
	id,
	value,
	onChange,
	placeholder = "Add text...",
	disabled = false,
	readOnly = false,
	autoFocus = false,
	status,
	className,
	minRows = 3,
	maxRows,
	variant = "outline",
	"aria-label": ariaLabel,
}: InputRTEProps) {
	const { status: formItemStatus } = useContext(FormItemInputContext)
	const mergedStatus = status || formItemStatus
	const isError = mergedStatus === "error"
	const isWarning = mergedStatus === "warning"
	const isOutline = variant === "outline"

	const [isLinkInputOpen, setIsLinkInputOpen] = useState(false)
	const minHeight = `${minRows * 16 + 16}px`
	const maxHeight = maxRows ? `${maxRows * 16 + 16}px` : undefined
	const isEditable = !disabled && !readOnly
	const canAutoFocus = Boolean(autoFocus && isEditable)

	const onChangeRef = useRef(onChange)
	onChangeRef.current = onChange
	const prevCanAutoFocusRef = useRef(canAutoFocus)

	const editorAttributes = useMemo(
		() => ({
			role: readOnly ? "document" : "textbox",
			...(ariaLabel ? { "aria-label": ariaLabel } : {}),
			...(!readOnly && disabled ? { "aria-disabled": "true" } : {}),
			...(!readOnly && isError ? { "aria-invalid": "true" } : {}),
		}),
		[ariaLabel, disabled, readOnly, isError],
	)

	const editorProps = useMemo(
		() => ({
			attributes: editorAttributes,
		}),
		[editorAttributes],
	)

	const extensions = useMemo(
		() => [
			...STATIC_EXTENSIONS,
			Placeholder.configure({
				placeholder,
				emptyEditorClass: "is-editor-empty",
			}),
		],
		[placeholder],
	)

	const [initialContent] = useState(() => normalizeRichText(value ?? ""))
	const lastSyncedValueRef = useRef(initialContent)

	const editor = useEditor({
		shouldRerenderOnTransaction: isEditable,
		extensions,
		content: initialContent,
		editable: isEditable,
		autofocus: canAutoFocus ? "end" : false,
		editorProps,
		onSelectionUpdate: () => {
			setIsLinkInputOpen(false)
		},
		onUpdate: ({ editor }) => {
			const html = isEditorEmpty(editor) ? "" : editor.getHTML()
			lastSyncedValueRef.current = html
			onChangeRef.current?.(html)
		},
	})

	// Sync external value changes
	useEffect(() => {
		if (!editor || value === undefined) {
			return
		}
		const normalized = normalizeRichText(value)
		if (normalized === lastSyncedValueRef.current) {
			return
		}
		lastSyncedValueRef.current = normalized
		const currentContent = isEditorEmpty(editor) ? "" : editor.getHTML()
		if (normalized !== currentContent) {
			editor.commands.setContent(normalized, { emitUpdate: false })
		}
	}, [value, editor])

	// Sync editable state, attributes, and focus on transition
	useEffect(() => {
		if (!editor) {
			return
		}
		const shouldFocus = !prevCanAutoFocusRef.current && canAutoFocus
		prevCanAutoFocusRef.current = canAutoFocus

		if (editor.isEditable !== isEditable) {
			editor.setEditable(isEditable, false)
		}
		editor.setOptions({
			editorProps,
		})
		if (shouldFocus) {
			editor.commands.focus("end")
		}
	}, [isEditable, canAutoFocus, editor, editorProps])

	return (
		<div
			id={id}
			className={cn(
				"relative w-full rounded-lg px-2.75 py-1 transition-colors",
				isOutline && [
					"border bg-white",
					!isError && !isWarning && "border-grey-20",
					!isError &&
						!isWarning &&
						!disabled &&
						!readOnly &&
						"focus-within:border-green-60 hover:border-green-60",
					isError &&
						"border-red-60 focus-within:border-red-60 hover:border-red-60",
					isWarning &&
						"border-orange-60 focus-within:border-orange-60 hover:border-orange-60",
					disabled && "border-grey-20",
				],
				disabled && "cursor-not-allowed bg-grey-10 opacity-60",
				className,
			)}
		>
			{editor && isEditable && (
				<BubbleMenu
					editor={editor}
					className="flex items-center gap-0.5 rounded-xl border border-grey-20 bg-white p-0.5 shadow-base"
				>
					{isLinkInputOpen ? (
						<LinkSettings
							editor={editor}
							onClose={() => setIsLinkInputOpen(false)}
						/>
					) : (
						<>
							<MenuButton
								aria-label="Bold"
								aria-pressed={editor.isActive("bold")}
								icon={<BoldOutlined aria-hidden />}
								onClick={() => editor.chain().focus().toggleBold().run()}
							/>
							<MenuButton
								aria-label="Italic"
								aria-pressed={editor.isActive("italic")}
								icon={<ItalicOutlined aria-hidden />}
								onClick={() => editor.chain().focus().toggleItalic().run()}
							/>
							<Divider vertical className="top-0 mx-0.5 h-[stretch]" />
							<MenuButton
								aria-label="Bullet list"
								aria-pressed={editor.isActive("bulletList")}
								icon={<UnorderedListOutlined aria-hidden />}
								onClick={() => editor.chain().focus().toggleBulletList().run()}
							/>
							<MenuButton
								aria-label="Numbered list"
								aria-pressed={editor.isActive("orderedList")}
								icon={<OrderedListOutlined aria-hidden />}
								onClick={() => editor.chain().focus().toggleOrderedList().run()}
							/>
							<Divider vertical className="top-0 mx-0.5 h-[stretch]" />
							<MenuButton
								aria-label="Link"
								aria-pressed={editor.isActive("link")}
								icon={<LinkOutlined aria-hidden />}
								onClick={() => setIsLinkInputOpen(true)}
							/>
						</>
					)}
				</BubbleMenu>
			)}

			<EditorContent
				editor={editor}
				style={{ minHeight, maxHeight }}
				className={cn(
					"prose prose-sm max-w-none text-navy-100 outline-none",
					maxRows && "overflow-y-auto",
					"[&_.ProseMirror]:min-h-[inherit] [&_.ProseMirror]:leading-normal [&_.ProseMirror]:outline-none",
					maxRows && "[&_.ProseMirror]:max-h-[inherit]",
					"[&_.ProseMirror_p.is-editor-empty:first-child]:before:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child]:before:float-left [&_.ProseMirror_p.is-editor-empty:first-child]:before:h-0 [&_.ProseMirror_p.is-editor-empty:first-child]:before:text-navy-40 [&_.ProseMirror_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]",
				)}
			/>
		</div>
	)
}

export function normalizeRichText(content?: string): string {
	if (!content) {
		return ""
	}

	const hasHtmlTags =
		/<\/(p|div|ul|ol|li|strong|b|em|i|u|s|a|h[1-6]|span|blockquote)>|<(br|hr)\s*\/?>/i.test(
			content,
		) || /^\s*<(p|div|ul|ol|blockquote)(\s[^>]*)?>/i.test(content)

	if (hasHtmlTags) {
		return content
	}

	const escaped = content
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")

	const lines = escaped
		.split(/\r?\n/)
		.map((line) => line.trim().replace(/[ \t]+/g, " "))
		.filter(Boolean)

	if (lines.length === 0) {
		return ""
	}

	return lines.map((line) => `<p>${line}</p>`).join("")
}

function MenuButton({
	icon,
	className,
	"aria-pressed": ariaPressed,
	...props
}: ComponentProps<"button"> & {
	icon: ReactNode
}) {
	return (
		<button
			{...props}
			type="button"
			aria-pressed={ariaPressed}
			className={cn(
				"flex size-7 items-center justify-center rounded-lg text-sm text-navy-100 transition-colors hover:bg-grey-10",
				ariaPressed && "bg-grey-30 hover:bg-grey-30",
				className,
			)}
		>
			{icon}
		</button>
	)
}

function LinkSettings({
	editor,
	onClose,
}: {
	editor: Editor
	onClose: VoidFunction
}) {
	const [url, setUrl] = useState(() => editor.getAttributes("link").href || "")
	const inputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		inputRef.current?.focus()
		inputRef.current?.select()
	}, [])

	const handleApply = useCallback(() => {
		const trimmed = url.trim()
		if (!trimmed) {
			editor.chain().focus().extendMarkRange("link").unsetLink().run()
		} else {
			const formatted =
				/^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed)
					? trimmed
					: `https://${trimmed}`
			editor
				.chain()
				.focus()
				.extendMarkRange("link")
				.setLink({ href: formatted })
				.run()
		}
		onClose()
	}, [editor, onClose, url])

	const handleRemove = useCallback(() => {
		editor.chain().focus().extendMarkRange("link").unsetLink().run()
		onClose()
	}, [editor, onClose])

	const handleCancel = useCallback(() => {
		editor.commands.focus()
		onClose()
	}, [editor, onClose])

	return (
		<div
			role="group"
			className="focus-outline flex items-center gap-1 px-1 py-0.5"
			aria-label="Link settings"
		>
			<input
				type="url"
				value={url}
				ref={inputRef}
				aria-label="Link URL"
				placeholder="Paste a link..."
				className="h-6.5 w-48 rounded-lg border border-grey-30 px-2 text-xs text-navy-100 outline-none hover:border-green-60 focus:border-green-60"
				onChange={(e) => setUrl(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault()
						handleApply()
					} else if (e.key === "Escape") {
						e.preventDefault()
						handleCancel()
					}
				}}
			/>
			<Button
				size="small"
				type="primary"
				aria-label="Apply link"
				icon={<CheckOutlined aria-hidden />}
				onClick={handleApply}
			/>
			{editor.isActive("link") && (
				<Button
					size="small"
					danger
					aria-label="Remove link"
					icon={<DisconnectOutlined aria-hidden />}
					onClick={handleRemove}
				/>
			)}
			<Button
				size="small"
				aria-label="Cancel"
				icon={<CloseOutlined aria-hidden />}
				onClick={handleCancel}
			/>
		</div>
	)
}

const STATIC_EXTENSIONS = [
	StarterKit.configure({
		heading: false,
		codeBlock: false,
		blockquote: false,
		code: false,
		strike: false,
		horizontalRule: false,
		link: false,
		hardBreak: false,
		bulletList: {
			HTMLAttributes: {
				class: "list-disc pl-3.5",
			},
		},
		orderedList: {
			HTMLAttributes: {
				class: "list-decimal pl-3.5",
			},
		},
	}),
	HardBreak.extend({
		addKeyboardShortcuts() {
			return {
				"Shift-Enter": () => this.editor.commands.setHardBreak(),
			}
		},
	}),
	LinkExtension.configure({
		openOnClick: false,
		autolink: true,
		linkOnPaste: true,
		defaultProtocol: "https",
		HTMLAttributes: {
			class: "text-green-60 underline hover:text-green-80 cursor-pointer",
			target: "_blank",
			rel: "noopener noreferrer",
		},
	}),
]

function isEditorEmpty(editor: Editor | null): boolean {
	return !editor || editor.isEmpty || editor.getText().trim().length === 0
}
